import { NextRouter } from 'next/router';
import { ApolloClient } from '@apollo/client';

import { ERNIE_TYPES } from 'shared/constants';
import {
  getDefaultRewardsProgramDisplayName,
  getDefaultRewardsProgramDisplayDescription,
} from 'shared/core/helpers/dispensaries';
import { isReferralsRoute as isReferralsRouteUtils } from 'src/components/loyalty/rewards/dpos-utils';
import { CreateDutchiePosLoyaltyMemberDocument, CreateRedemptionDocument, GetRewardsV2Document } from 'src/gql/graphql';
import type { Dispensaries, FindOrCreateReferralCodeMutation } from 'src/gql/graphql';
import type { UseLoyaltyEnrollmentAnalyticsReturn } from 'src/hooks/use-loyalty-enrollment-analytics';
import type { LoyaltyEnrollmentSurface } from 'src/utils/analytics/events';
import { getVerificationRequiredPrompt, Statuses, UseLoyaltyReturn, ShowErnie, CreateUserAndPinOptions } from './types';

type Dependencies = {
  dispensary: Dispensaries;
  user: {
    id: string;
    phone: string;
    birthday: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  apolloClient: ApolloClient<object>;
  showErnie: ShowErnie;
  analytics: UseLoyaltyEnrollmentAnalyticsReturn;
};

type DutchiePOSLoyaltyProps = {
  status: Statuses;
  setStatus: (status: Statuses) => void;
  deps: Dependencies;
  router: NextRouter | null;
  surface?: LoyaltyEnrollmentSurface | null;
  referralData?: FindOrCreateReferralCodeMutation['findOrCreateReferralCode'] | null;
};

export const createDutchiePosLoyalty = ({
  status,
  setStatus,
  deps,
  router,
  surface = 'accountPanel',
  referralData,
}: DutchiePOSLoyaltyProps): UseLoyaltyReturn => {
  const { dispensary, user: User, apolloClient, showErnie, analytics } = deps;

  const isReferralsRoute = router ? isReferralsRouteUtils(router) : false;
  const referralCode = router?.isReady && typeof router.query.code === 'string' ? router.query.code : '';

  const programName =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    dispensary.storeSettings.rewardsIntegrationConfiguration?.rewardsProgramDisplayName ||
    getDefaultRewardsProgramDisplayName({ location: dispensary.location ?? {} });
  const programDescription =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    dispensary.storeSettings.rewardsIntegrationConfiguration?.rewardsProgramDisplayDescription ||
    getDefaultRewardsProgramDisplayDescription({ location: dispensary.location ?? {} });

  const createUserAndPin = async ({ afterVerification = false }: CreateUserAndPinOptions = {}): Promise<void> => {
    setStatus(Statuses.loading);
    // Post-verification retry continues the original submission; don't double-count it.
    if (!afterVerification) {
      analytics.trackSubmitted();
    }
    try {
      await apolloClient.mutate({
        mutation: CreateDutchiePosLoyaltyMemberDocument,
        variables: {
          senderUniqueId: referralData?.senderPosUniqueId ?? '',
          dispensaryId: dispensary.id,
          userId: User.id,
        },
        ...(isReferralsRoute && { refetchQueries: [GetRewardsV2Document] }),
      });

      analytics.trackSucceeded();

      if (isReferralsRoute && referralCode) {
        await createReferralRedemption();
      }

      setStatus(Statuses.success);
    } catch (error) {
      const verificationRequiredPrompt = getVerificationRequiredPrompt(error);

      if (verificationRequiredPrompt) {
        if (surface === null || afterVerification) {
          showErnie(verificationRequiredPrompt, ERNIE_TYPES.DANGER);
        }
        if (afterVerification) {
          analytics.trackFailed();
          setStatus(Statuses.terms);
        } else {
          setStatus(Statuses.verificationRequired);
        }
        return;
      }

      analytics.trackFailed();
      showErnie('Error connecting account, please try again.', ERNIE_TYPES.DANGER);
      setStatus(Statuses.terms);
    }
  };

  const createReferralRedemption = async (): Promise<void> => {
    setStatus(Statuses.loading);
    try {
      await apolloClient.mutate({
        mutation: CreateRedemptionDocument,
        variables: {
          code: referralCode,
          receiverUserId: User.id,
        },
      });

      showErnie('Success! Complete an order to also receive a Referral Reward.', ERNIE_TYPES.SUCCESS);
      setStatus(Statuses.success);

      const { customDomainSettings, cName } = dispensary;
      const hostUrl = window.location.host;

      if (cName) {
        let redirectUrl = `https://${hostUrl}/stores/${cName}`;

        const customDomain = customDomainSettings?.domain ?? '';

        if (customDomain) {
          redirectUrl = `https://${customDomain}/stores/${cName}`;
        }

        window.location.href = redirectUrl;
      }
    } catch {
      showErnie('Error creating redemption. Please try again.', ERNIE_TYPES.DANGER);
    }
  };

  return {
    status,
    setStatus,
    programName,
    programDescription,
    createUserAndPin,
    processPin: async () => Promise.resolve(),
    resendPin: async () => Promise.resolve(),
  };
};
